import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SpeedIcon from '@mui/icons-material/Speed';
import MemoryIcon from '@mui/icons-material/Memory';
import BoltIcon from '@mui/icons-material/Bolt';
import { PageContainer } from '../components/AdminContentWrapper';
import CreditsEngineService from '../../../services/CreditsEngineService';
import { describeModel, markRecommended } from '../../../utils/llmModels';

// Re-export: los tests y otros usos importaban describeModel desde esta página.
export { describeModel, markRecommended };

const AgentsModelsLLM = () => {
  const [models, setModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const configured = await CreditsEngineService.listConfiguredModels();
        if (!cancelled) setModels(markRecommended(configured.map(describeModel)));
      } catch (err) {
        if (!cancelled) setError('No fue posible obtener los modelos configurados.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PageContainer
      title="Modelos LLM disponibles"
      description="Explorá los modelos de lenguaje disponibles para tus agentes y las capacidades de cada uno."
    >
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
        </Box>
      )}

      {!isLoading && error && (
        <Typography variant="body2" sx={{ color: 'var(--wb-danger)', py: 2 }}>
          {error}
        </Typography>
      )}

      {!isLoading && !error && models.length === 0 && (
        <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)', py: 2 }}>
          Todavía no hay modelos configurados en la plataforma.
        </Typography>
      )}

      <Grid container spacing={3}>
        {models.map((model) => (
          <Grid item xs={12} md={6} lg={4} key={model.id}>
            <Box sx={{ position: 'relative', pt: model.recommended ? 2 : 0 }}>
              {model.recommended && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: model.color,
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    boxShadow: 2,
                    zIndex: 1
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    RECOMENDADO
                  </Typography>
                </Box>
              )}

              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: model.recommended ? 2 : 1,
                  borderColor: model.recommended ? model.color : 'divider',
                  transition: 'all 0.3s ease',
                  mt: model.recommended ? 1.5 : 0,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, pt: model.recommended ? 3 : 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 600, color: model.color, wordBreak: 'break-word' }}
                    >
                      {model.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {model.version ? `${model.provider} • ${model.version}` : model.provider}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {model.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {model.capabilities.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Capacidades
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {model.capabilities.map((cap, idx) => (
                          <Chip
                            key={idx}
                            label={cap}
                            size="small"
                            sx={{
                              bgcolor: `${model.color}15`,
                              color: model.color,
                              fontWeight: 500
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                    {model.contextWindow && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <MemoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          <strong>Contexto:</strong> {model.contextWindow}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <SpeedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        <strong>Rendimiento:</strong> {model.performance}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BoltIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        <strong>Velocidad:</strong> {model.speed}
                      </Typography>
                    </Box>
                  </Paper>

                  <Box sx={{ mt: 2, p: 1, bgcolor: `${model.color}08`, borderRadius: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                    >
                      ID: {model.id}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} sx={{ mt: 4, p: 3, bgcolor: 'info.lighter' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Notas importantes
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          • Solo se muestran los modelos configurados y activos en la plataforma
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          • Fable y Opus ofrecen las mejores capacidades para análisis complejos; Sonnet equilibra capacidad y velocidad; Haiku prioriza la rapidez
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Entre versiones de una misma familia, la más reciente suele dar mejores respuestas
        </Typography>
      </Paper>
    </PageContainer>
  );
};

export default AgentsModelsLLM;
